/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FileText, FilePlus, Filter, Eye, Send, Trash2, ArrowRight, Receipt, Edit3, BadgeCheck, PenTool, X, Search, MoreHorizontal, Clock } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";
import { DatePickerField } from "@/components/location/DateTimeFields";

const STATUS_LABELS = {
  brouillon: "Brouillon",
  envoye: "Envoyé",
  envoyee: "Envoyée",
  accepte: "Accepté",
  signe: "Signé",
  acompte_recu: "Acompte reçu",
  refuse: "Refusé",
  expire: "Expiré",
  payee: "Payée",
  partielle: "Partiellement payée",
  impayee: "Impayée",
  annulee: "Annulée",
};

function statusBadge(status) {
  const positive = ["accepte", "payee", "envoye", "envoyee", "signe", "acompte_recu"];
  const negative = ["refuse", "expire", "impayee", "annulee"];
  const colorClass = positive.includes(status)
    ? "text-[#DCB457] border-[#DCB457]/40 bg-[#DCB457]/8"
    : negative.includes(status)
    ? "text-red-300 border-red-400/30 bg-red-500/8"
    : "text-white/55 border-white/15 bg-white/3";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] border px-2 py-0.5 ${colorClass}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function fmtEur(v) {
  const n = Number(v || 0);
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}

/**
 * DevisPipeline — small horizontal step-tracker that shows where the file
 * stands in the client journey. Steps for a devis:
 *   1. Créé  →  2. Envoyé  →  3. Consulté  →  4. Signé  →  5. Acompte reçu
 * Steps for a facture:
 *   1. Créée →  2. Envoyée →  3. Payée
 */
const FOLLOWUP_DAYS = 7;
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);
const needsFollowUp = (d) =>
  d.doc_type === "devis" && !d.is_signed && ["envoye", "accepte"].includes(d.status) && daysSince(d.sent_at) >= FOLLOWUP_DAYS;
const norm = (v) => (v || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function DevisPipeline({ doc }) {
  const isDevis = doc.doc_type === "devis";
  const steps = isDevis
    ? [
        { key: "cree", label: "Créé", done: true },
        { key: "envoye", label: "Envoyé", done: !!doc.sent_at || ["envoye", "envoyee", "signe", "acompte_recu"].includes(doc.status) },
        { key: "consulte", label: "Consulté", done: !!doc.viewed_at || !!doc.is_signed },
        { key: "signe", label: "Signé", done: !!doc.is_signed },
        { key: "acompte", label: "Acompte", done: !!doc.acompte_invoice_id || doc.status === "acompte_recu" },
      ]
    : [
        { key: "cree", label: "Créée", done: true },
        { key: "envoye", label: "Envoyée", done: !!doc.sent_at || ["envoyee", "payee", "partielle"].includes(doc.status) },
        { key: "payee", label: "Payée", done: doc.status === "payee" },
      ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const currentLabel = steps[Math.max(0, doneCount - 1)]?.label || steps[0].label;
  return (
    <div className="w-[90px]" data-testid={`pipeline-${doc.id}`} title={`${currentLabel} — ${doneCount}/${steps.length} · ${pct}%`}>
      <div className="flex items-center gap-[2px]">
        {steps.map((s) => (
          <div
            key={s.key}
            className={`flex-1 h-[3px] rounded-full transition-colors ${s.done ? "bg-[#DCB457]" : "bg-white/12"}`}
          />
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between gap-1 text-[9px] uppercase tracking-[0.1em]">
        <span className={`truncate ${pct === 100 ? "text-[#DCB457]" : "text-white/45"}`}>{currentLabel}</span>
        <span className="text-white/40 shrink-0">{doneCount}/{steps.length}</span>
      </div>
    </div>
  );
}

export default function AdminDevis() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | devis | facture
  const [query, setQuery] = useState("");
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const followUpCount = items.filter(needsFollowUp).length;
  const shown = useMemo(() => {
    const q = norm(query.trim());
    return items.filter((d) => {
      if (followUpOnly && !needsFollowUp(d)) return false;
      if (!q) return true;
      return norm([d.number, d.client_name, d.client_email, d.raison_sociale, d.nom_commercial, d.event_location].join(" ")).includes(q);
    });
  }, [items, query, followUpOnly]);

  const load = () => {
    setLoading(true);
    adminApi.listBilling(filter === "all" ? {} : { doc_type: filter })
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const sendDoc = async (id) => {
    try {
      await adminApi.sendBilling(id);
      toast.success("Document envoyé au client par email.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const convertDoc = async (id) => {
    try {
      const res = await adminApi.convertBilling(id);
      toast.success(`Facture ${res.number} créée.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const markAcompte = async (d) => {
    if (!window.confirm(`Confirmer la réception de l'acompte de ${fmtEur(d.deposit_ttc)} pour ${d.number} ?\n\nUne facture d'acompte sera générée et envoyée automatiquement par email au client.`)) return;
    try {
      const res = await adminApi.markAcompteReceived(d.id);
      toast.success(`Facture d'acompte ${res.number} créée et envoyée.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [showManualSign, setShowManualSign] = useState(null); // holds the doc being validated
  const [manualForm, setManualForm] = useState({ signed_name: "", signed_at: "", note: "", notify_client: false });
  const [manualBusy, setManualBusy] = useState(false);

  const openManualSign = (d) => {
    setManualForm({
      signed_name: d.client_name || "",
      signed_at: new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Paris" }),
      note: "",
      notify_client: false,
    });
    setShowManualSign(d);
  };
  const submitManualSign = async () => {
    if (!showManualSign) return;
    setManualBusy(true);
    try {
      const payload = {
        signed_name: manualForm.signed_name.trim() || showManualSign.client_name,
        signed_at: manualForm.signed_at ? new Date(manualForm.signed_at).toISOString() : undefined,
        note: manualForm.note.trim(),
        notify_client: !!manualForm.notify_client,
      };
      const res = await adminApi.markSigned(showManualSign.id, payload);
      toast.success(`Devis ${res.doc?.number || showManualSign.number} marqué comme signé.`);
      setShowManualSign(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setManualBusy(false);
    }
  };

  const delDoc = async (id, label) => {
    if (!window.confirm(`Supprimer ${label} ?`)) return;
    try {
      await adminApi.deleteBilling(id);
      toast.success("Document supprimé.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const openPdf = (id) => {
    const token = localStorage.getItem("ma_token");
    fetch(adminApi.billingPdfUrl(id), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error("Impossible de récupérer le PDF."));
  };

  // Stats
  const totalAccepted = items.filter((d) => d.doc_type === "devis" && d.status === "accepte").reduce((s, d) => s + (d.total_ttc || 0), 0);
  const totalPaid = items.filter((d) => d.doc_type === "facture" && d.status === "payee").reduce((s, d) => s + (d.total_ttc || 0), 0);
  const pending = items.filter((d) => ["envoye", "envoyee"].includes(d.status)).length;

  return (
    <div data-testid="admin-devis">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Facturation</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Devis & <span className="italic gold-text">Factures.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Génération PDF dark/gold, envoi par email, conversion devis → facture en 1 clic.
          </p>
        </div>
        <Link to="/admin/devis/nouveau" data-testid="admin-devis-new-btn" className="btn-gold inline-flex items-center gap-2">
          <FilePlus size={14} strokeWidth={1.5} /> Nouveau devis
        </Link>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatTile icon={FileText} label="Devis acceptés" value={fmtEur(totalAccepted)} sub="Cumul TTC" />
        <StatTile icon={Receipt} label="Factures payées" value={fmtEur(totalPaid)} sub="Cumul TTC" />
        <StatTile icon={Send} label="En attente de réponse" value={pending} sub="Devis/factures envoyés" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter size={14} strokeWidth={1.4} className="text-[#DCB457]" />
        {[["all", "Tous"], ["devis", "Devis"], ["facture", "Factures"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`admin-devis-filter-${k}`}
            className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition ${filter === k ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/8" : "border-white/12 text-white/55 hover:border-[#DCB457]/40"}`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setFollowUpOnly((v) => !v)}
          data-testid="admin-devis-filter-followup"
          className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${followUpOnly ? "border-amber-300 text-amber-200 bg-amber-400/10" : "border-white/12 text-white/55 hover:border-amber-300/50"}`}
        >
          <Clock size={11} strokeWidth={1.5} /> À relancer ({followUpCount})
        </button>
        <div className="relative ml-auto w-full md:w-80">
          <Search size={13} strokeWidth={1.4} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Client, société ou n° de document…" className="w-full pl-8 pr-3 py-2 bg-black/50 border border-[#DCB457]/25 text-sm focus:border-[#DCB457] outline-none" data-testid="admin-devis-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-luxe overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-white/50 italic">Chargement…</p>
        ) : items.length > 0 && shown.length === 0 ? (
          <p className="p-10 text-center text-white/45 italic" data-testid="admin-devis-no-result">Aucun document ne correspond.</p>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt size={36} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
            <p className="text-white/65">Aucun document.</p>
            <Link to="/admin/devis/nouveau" className="btn-ghost-gold mt-6 inline-flex">Créer le premier devis</Link>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55">
              <tr>
                <th className="px-3 py-3 text-left">N°</th>
                <th className="px-3 py-3 text-left">Client</th>
                <th className="px-3 py-3 text-left">Événement</th>
                <th className="px-3 py-3 text-right">Total TTC</th>
                <th className="px-3 py-3 text-left">Statut</th>
                <th className="px-3 py-3 text-left">Avancement</th>
                <th className="px-3 py-3 text-left">Émis le</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => (
                <tr key={d.id} className="border-t border-[#DCB457]/10" data-testid={`admin-devis-row-${d.number}`}>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <Link to={`/admin/devis/${d.id}`} className="font-mono text-xs text-[#DCB457] hover:underline">
                      {d.number}
                    </Link>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 mt-0.5">
                      {d.doc_type === "devis" ? "Devis" : "Facture"}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-white/85">{d.client_name}</div>
                    {d.raison_sociale && <div className="text-[11px] text-[#DCB457]/80">{d.raison_sociale}</div>}
                    {d.client_email && <div className="text-[11px] text-white/45">{d.client_email}</div>}
                  </td>
                  <td className="px-3 py-3 text-white/70 text-xs">
                    {d.event_type} {d.event_date && `· ${fmtDate(d.event_date)}`}
                    {d.event_location && <div className="text-[11px] text-white/40">{d.event_location}</div>}
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-[#DCB457] whitespace-nowrap">{fmtEur(d.total_ttc)}</td>
                  <td className="px-3 py-3">
                    {statusBadge(d.status)}
                    {needsFollowUp(d) && (
                      <div className="mt-1.5 text-[10px] text-amber-200/90 inline-flex items-center gap-1 whitespace-nowrap" data-testid={`admin-devis-followup-${d.id}`}>
                        <Clock size={10} strokeWidth={1.5} /> Sans réponse · {daysSince(d.sent_at)} j
                      </div>
                    )}
                    {d.status === "refuse" && d.refusal_reason && (
                      <div className="mt-1.5 text-[10px] text-white/50 italic max-w-[140px] line-clamp-2" title={d.refusal_reason} data-testid={`admin-devis-refusal-${d.id}`}>« {d.refusal_reason} »</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <DevisPipeline doc={d} />
                  </td>
                  <td className="px-3 py-3 text-white/65 text-xs whitespace-nowrap">{fmtDate(d.created_at)}</td>
                  <td className="px-2 py-3 text-right">
                    <div className="grid grid-cols-3 gap-1 w-max ml-auto [&>*]:justify-center" data-testid={`admin-devis-actions-${d.id}`}>
                      <button onClick={() => openPdf(d.id)} title="Voir le PDF" data-testid={`admin-devis-pdf-${d.id}`} className="inline-flex items-center text-[#DCB457] hover:bg-[#DCB457]/10 px-1.5 py-1.5 border border-[#DCB457]/25 text-xs">
                        <Eye size={11} strokeWidth={1.4} />
                      </button>
                      <Link to={`/admin/devis/${d.id}`} title="Éditer" data-testid={`admin-devis-edit-${d.id}`} className="inline-flex items-center text-white/70 hover:bg-white/5 px-1.5 py-1.5 border border-white/15 text-xs">
                        <Edit3 size={11} strokeWidth={1.4} />
                      </Link>
                      {d.client_email && (
                        <button onClick={() => sendDoc(d.id)} title="Envoyer par email" data-testid={`admin-devis-send-${d.id}`} className="inline-flex items-center text-[#DCB457] hover:bg-[#DCB457]/10 px-1.5 py-1.5 border border-[#DCB457]/25 text-xs">
                          <Send size={11} strokeWidth={1.4} />
                        </button>
                      )}
                      {d.doc_type === "devis" && !d.is_signed && (
                        <button
                          onClick={() => openManualSign(d)}
                          title="Marquer comme signé manuellement (signature papier / hors-ligne)"
                          data-testid={`admin-devis-manual-sign-${d.id}`}
                          className="inline-flex items-center text-[#DCB457] hover:bg-[#DCB457]/10 px-1.5 py-1.5 border border-[#DCB457]/25 text-xs"
                        >
                          <PenTool size={11} strokeWidth={1.4} />
                        </button>
                      )}
                      {d.doc_type === "devis" && d.is_signed && d.status !== "acompte_recu" && !d.acompte_invoice_id && (
                        <button onClick={() => markAcompte(d)} title="Marquer l'acompte comme reçu (génère + envoie la facture d'acompte)" data-testid={`admin-devis-acompte-${d.id}`} className="inline-flex items-center gap-1 text-emerald-300 hover:bg-emerald-500/10 px-1.5 py-1.5 border border-emerald-400/40 text-xs">
                          <BadgeCheck size={11} strokeWidth={1.4} />
                        </button>
                      )}
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button title="Plus d'actions" data-testid={`admin-devis-more-${d.id}`} className="inline-flex items-center text-white/70 hover:bg-white/5 px-1.5 py-1.5 border border-white/15 text-xs">
                            <MoreHorizontal size={11} strokeWidth={1.4} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#0a0a0a] border-[#DCB457]/30 text-white/85 rounded-none min-w-[190px]">
                          {d.doc_type === "devis" && (
                            <DropdownMenuItem onSelect={() => convertDoc(d.id)} data-testid={`admin-devis-convert-${d.id}`} className="text-xs gap-2 cursor-pointer focus:bg-[#DCB457]/10 focus:text-[#DCB457]">
                              <ArrowRight size={12} strokeWidth={1.4} /> Convertir en facture
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onSelect={() => delDoc(d.id, d.number)} data-testid={`admin-devis-del-${d.id}`} className="text-xs gap-2 cursor-pointer text-red-300 focus:bg-red-500/10 focus:text-red-200">
                            <Trash2 size={12} strokeWidth={1.4} /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Manual signature modal */}
      {showManualSign && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          data-testid="manual-sign-modal"
          onClick={() => !manualBusy && setShowManualSign(null)}
        >
          <div
            className="w-full max-w-md bg-[#0a0a0a] border border-[#DCB457]/30 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="eyebrow">Valider signature manuelle</p>
              <button
                type="button"
                onClick={() => setShowManualSign(null)}
                disabled={manualBusy}
                className="text-white/50 hover:text-white"
                data-testid="manual-sign-close"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
            <h2 className="font-serif-luxe text-2xl mb-4">
              Devis <span className="gold-text italic">{showManualSign.number}</span>
            </h2>
            <p className="text-xs text-white/55 mb-4">
              Utilisez ce formulaire uniquement si le client a signé <strong>hors ligne</strong> (papier, mail direct, avant ce système…).
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Signataire</label>
                <input
                  type="text"
                  value={manualForm.signed_name}
                  onChange={(e) => setManualForm({ ...manualForm, signed_name: e.target.value })}
                  data-testid="manual-sign-name"
                  className="input-luxe mt-1 w-full"
                  placeholder="Nom du signataire"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Date de signature</label>
                <DatePickerField value={manualForm.signed_at} onChange={(v) => setManualForm({ ...manualForm, signed_at: v })} className="mt-1" testid="manual-sign-date" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Note (facultatif)</label>
                <textarea
                  value={manualForm.note}
                  onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })}
                  data-testid="manual-sign-note"
                  className="input-luxe mt-1 w-full min-h-[70px]"
                  placeholder="Ex : signé sur place le jour du RDV, exemplaire papier archivé"
                />
              </div>
              <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={manualForm.notify_client}
                  onChange={(e) => setManualForm({ ...manualForm, notify_client: e.target.checked })}
                  data-testid="manual-sign-notify"
                  className="mt-0.5 accent-[#DCB457]"
                />
                <span>Envoyer aussi au client le mail de confirmation avec PDF signé</span>
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={submitManualSign}
                disabled={manualBusy || !manualForm.signed_name.trim()}
                data-testid="manual-sign-submit"
                className="btn-gold flex-1 py-2 text-sm disabled:opacity-60"
              >
                {manualBusy ? "Enregistrement…" : "Valider la signature"}
              </button>
              <button
                type="button"
                onClick={() => setShowManualSign(null)}
                disabled={manualBusy}
                className="border border-white/20 text-white/65 hover:bg-white/5 px-4 py-2 text-sm"
                data-testid="manual-sign-cancel"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="card-luxe p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">{label}</p>
        <Icon size={16} strokeWidth={1.3} className="text-[#DCB457]" />
      </div>
      <p className="font-serif-luxe text-3xl gold-text">{value}</p>
      <p className="text-[11px] text-white/45 mt-1">{sub}</p>
    </div>
  );
}
